import { useState } from "react";
import type { SkillFileDiff, SkillFinding } from "@roomate/core";
import { Action, Block, Card, Pill } from "../../calm.tsx";
import { cx, inputCls } from "../../ui.tsx";
import { canApprove, hookFindings, parseHosts, SOURCE_NOTE, type HookFinding, type HookVerdict, type HookVerdicts, type SkillVersionDTO } from "./skills.ts";

export type ApproveInput = { serviceHosts: string[]; acknowledgedFindingIds: string[]; hookVerdicts: HookVerdicts };

export function FindingRow({ finding, ack }: { finding: SkillFinding; ack?: { checked: boolean; onToggle: () => void } }) {
  return (
    <li className="flex flex-wrap items-start gap-2 px-4 py-3 text-[13px]" data-testid="skill-finding" data-kind={finding.kind}>
      {ack && <input type="checkbox" checked={ack.checked} onChange={ack.onToggle} aria-label={`Acknowledge ${finding.kind.replace(/_/g, " ")} at ${finding.file}`} data-testid={`ack-${finding.id}`} />}
      <Pill tone={finding.risk === "high" ? "danger" : "neutral"}>{finding.risk}</Pill>
      <span className="font-medium">{finding.kind.replace(/_/g, " ")}</span>
      <code className="font-mono text-[12px] text-ink-2" data-testid="finding-location">{finding.file}{finding.line ? `:${finding.line}` : ""}</code>
      <span className="basis-full text-ink-2">{finding.message}</span>
    </li>
  );
}

export function HookControl({ finding, verdict, disabled, onChange }: { finding: HookFinding; verdict: HookVerdict | undefined; disabled?: boolean; onChange: (v: HookVerdict) => void }) {
  const { hookId, event, matcher, command } = finding.detail;
  return (
    <li className="rounded-[16px] border border-rule bg-paper/40 p-4" data-testid="hook-item" data-hook={hookId}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2 text-[13px]">
          <Pill tone="danger">hook</Pill>
          <span className="font-medium" data-testid="hook-event">{event}</span>
          <span className="text-ink-3">matcher</span>
          <code className="font-mono text-[12px]" data-testid="hook-matcher">{matcher || "(any)"}</code>
        </div>
        {disabled && verdict && <Pill tone={verdict === "approve" ? "live" : "danger"} testId="hook-verdict">{verdict === "approve" ? "approved" : "rejected"}</Pill>}
        {!disabled && (
          <div className="flex gap-2" role="group" aria-label={`Verdict for ${event} hook`}>
            {(["approve", "reject"] as const).map((v) => (
              <button
                key={v}
                type="button"
                aria-pressed={verdict === v}
                onClick={() => onChange(v)}
                data-testid={`hook-${v}-${hookId}`}
                className={cx(
                  "rounded-full px-4 py-2 text-[13.5px] transition-colors",
                  verdict === v ? (v === "approve" ? "bg-ink text-paper" : "bg-high text-paper") : "bg-ink/[0.05] text-ink hover:bg-ink/[0.09]",
                )}
              >
                {v === "approve" ? "Approve" : "Reject"}
              </button>
            ))}
          </div>
        )}
      </div>
      <code className="mt-3 block whitespace-pre-wrap break-all rounded-md bg-ink px-3 py-2 font-mono text-[12px] leading-relaxed text-paper" data-testid="hook-command">{command}</code>
      <p className="mt-1.5 text-[11.5px] text-ink-3">{finding.file}{finding.line ? `:${finding.line}` : ""}</p>
    </li>
  );
}

export function FileDiffView({ diff }: { diff: SkillFileDiff[] }) {
  if (!diff.length) return <p className="text-[13px] italic text-ink-3">No file changes against the approved version.</p>;
  return (
    <div className="space-y-4" data-testid="skill-diff">
      {diff.map((file) => (
        <div key={file.path} data-testid="skill-diff-file" data-path={file.path}>
          <h4 className="mb-1.5 flex items-center gap-2 font-mono text-[12.5px]">{file.path}<Pill tone={file.status === "removed" ? "danger" : file.status === "added" ? "live" : "neutral"}>{file.status}</Pill></h4>
          <pre className="overflow-x-auto rounded-[14px] bg-ink p-4 font-mono text-[12px] leading-relaxed text-paper">
            {file.diff.split("\n").map((line, i) => (
              <span key={i} className={cx("block", line.startsWith("+") && !line.startsWith("+++") && "text-low", line.startsWith("-") && !line.startsWith("---") && "text-high")}>{line || " "}</span>
            ))}
          </pre>
        </div>
      ))}
    </div>
  );
}

/** The review screen: full sanitized files, findings, hook verdicts, service hosts, diff, approve/reject. Non-admins get the same content read-only. */
export function SkillReview({
  version, isAdmin, busy, locked, initialVerdicts, onApprove, onReject,
}: {
  version: SkillVersionDTO;
  isAdmin: boolean;
  busy?: boolean;
  /** True once approval was sent for risk acceptance: the version is still pending but must not be approved again. */
  locked?: boolean;
  initialVerdicts?: HookVerdicts;
  onApprove: (input: ApproveInput) => void;
  onReject: (reason: string) => void;
}) {
  const [verdicts, setVerdicts] = useState<HookVerdicts>(
    () => initialVerdicts ?? (version.status === "pending" ? {} : Object.fromEntries(hookFindings(version.findings).map((h) => [h.detail.hookId, version.approvedHookIds.includes(h.detail.hookId) ? "approve" : "reject"]))),
  );
  const [hosts, setHosts] = useState(version.serviceHosts.join(", "));
  const [reason, setReason] = useState("");
  // Findings the admin has ticked as seen. Sent only as ticked; a hook is covered by its verdict instead.
  const [acked, setAcked] = useState<Set<string>>(new Set());
  const toggleAck = (id: string) => setAcked((cur) => { const n = new Set(cur); if (!n.delete(id)) n.add(id); return n; });
  const hooks = hookFindings(version.findings);
  const decided = version.status !== "pending";
  const skillMd = version.files.find((f) => f.path === "SKILL.md");
  const others = version.files.filter((f) => f.path !== "SKILL.md");
  const ready = canApprove(version.findings, verdicts);
  const canDecide = isAdmin && !decided && !locked;
  const missing = hooks.filter((h) => verdicts[h.detail.hookId] === undefined).length;
  return (
    <div className="space-y-8" data-testid="skill-review">
      <Card>
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-[15px] font-medium">{version.name}</span>
          <Pill tone="neutral">v{version.version}</Pill>
          <Pill tone={version.status === "approved" ? "live" : version.status === "rejected" ? "danger" : "signal"} testId="version-status">{version.status}</Pill>
        </div>
        <p className="mt-2 break-all font-mono text-[12px] text-ink-2" data-testid="skill-review-source">{version.source} · <span title="Commit SHA or zip sha256">{version.sourceRef}</span></p>
        {version.rejectReason && <p className="mt-2 text-[13px] text-high">Rejected: {version.rejectReason}</p>}
      </Card>

      {version.diff && (
        <Block title="Changes against the approved version" aside="Per file">
          <FileDiffView diff={version.diff} />
        </Block>
      )}

      <Block title="Findings" aside={`${version.findings.length}`}>
        {version.findings.length ? (
          <Card pad={false}><ul className="divide-y divide-rule" data-testid="skill-findings">{version.findings.map((f) => <FindingRow key={f.id} finding={f} ack={canDecide && f.risk === "high" && f.kind !== "hook" ? { checked: acked.has(f.id), onToggle: () => toggleAck(f.id) } : undefined} />)}</ul></Card>
        ) : (
          <p className="text-[13px] italic text-ink-3">No findings.</p>
        )}
      </Block>

      {hooks.length > 0 && (
        <Block title="Hooks" aside="Each hook needs its own verdict. They run silently on every tool call.">
          <ul className="space-y-3" data-testid="skill-hooks">
            {hooks.map((h) => (
              <HookControl key={h.id} finding={h} verdict={verdicts[h.detail.hookId]} disabled={!canDecide} onChange={(v) => setVerdicts((cur) => ({ ...cur, [h.detail.hookId]: v }))} />
            ))}
          </ul>
        </Block>
      )}

      <Block title="Service hosts" aside={SOURCE_NOTE}>
        <Card>
          {canDecide ? (
            <input className={inputCls} value={hosts} onChange={(e) => setHosts(e.target.value)} placeholder="api.github.com, api.example.com" aria-label="Service hosts" data-testid="skill-service-hosts" />
          ) : (
            <p className="font-mono text-[12.5px]" data-testid="skill-service-hosts-readonly">{version.serviceHosts.join(", ") || "none"}</p>
          )}
          <p className="mt-2 text-[12px] text-ink-3" data-testid="hosts-note">{SOURCE_NOTE}</p>
        </Card>
      </Block>

      <Block title="Files" aside={`${version.files.length} · read-only`}>
        <div className="space-y-3" data-testid="skill-files">
          {skillMd && <SkillFile path={skillMd.path} text={skillMd.text} />}
          {others.map((f) => <SkillFile key={f.path} path={f.path} text={f.text} />)}
        </div>
      </Block>

      {canDecide && (
        <div className="sticky bottom-4 space-y-3 rounded-[18px] border border-rule bg-card/95 p-4 shadow-xl backdrop-blur" data-testid="skill-decision-bar">
          <input className={inputCls} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (required to reject)" aria-label="Reject reason" data-testid="skill-reject-reason" />
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-[12.5px] text-ink-2">{ready ? "Ready to approve." : `${missing} hook${missing === 1 ? "" : "s"} still need a verdict.`}</p>
            <div className="flex gap-2">
              <Action danger disabled={busy || !reason.trim()} onClick={() => onReject(reason.trim())} testId="skill-reject">Reject</Action>
              <Action primary disabled={busy || !ready} onClick={() => onApprove({ serviceHosts: parseHosts(hosts), acknowledgedFindingIds: [...acked], hookVerdicts: verdicts })} testId="skill-approve">Approve</Action>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SkillFile({ path, text }: { path: string; text: string }) {
  return (
    <div className="rounded-[14px] bg-ink/[0.035] p-3" data-testid="skill-file" data-path={path}>
      <code className="font-mono text-[12px] font-medium">{path}</code>
      <pre className="mt-2 max-h-96 overflow-auto whitespace-pre-wrap font-mono text-[11.5px] leading-relaxed text-ink-2">{text}</pre>
    </div>
  );
}
